import * as React from 'react';
import { ListFilter } from 'lucide-react';
import { Button, MultiSelect, SlideOver, Input, type DropdownSelectOption } from '@/components/ui';
import { getTicketFields, type TicketFieldRegistryField } from '@/lib/ticket-field-registry-api';
import { STATUS_MAP, PRIORITY_MAP, SOURCE_MAP, localizeLabel } from '@/lib/constants';
import { useI18n } from '@/lib/i18n';
import { filterGroupsByCountryAndLevel, filterLevelsByCountry } from '@/lib/ticket-group-options';

export { filterGroupsByLevel } from '@/lib/ticket-group-options';

// Full multi-section ticket filter drawer (Freshdesk-style "Filters" panel):
// built-in fields (agents, groups, statuses, priorities, sources, tags,
// companies, contacts, date ranges) plus every active dropdown/checkbox/
// radio/multiselect field from the ticket field registry, so account-
// specific custom fields (Bug Type, CR Type, Issue Bucket, etc.) show up
// automatically without a hardcoded list.

export interface TicketFilterState {
  statuses: string[];
  priorities: string[];
  sources: string[];
  agentIds: string[];
  groupIds: string[];
  companyIds: string[];
  ticketTypeIds: string[];
  tagIds: string[];
  createdFrom: string;
  createdTo: string;
  resolvedFrom: string;
  resolvedTo: string;
  closedFrom: string;
  closedTo: string;
  customFields: Record<string, string[]>;
}

export const EMPTY_TICKET_FILTERS: TicketFilterState = {
  statuses: [],
  priorities: [],
  sources: [],
  agentIds: [],
  groupIds: [],
  companyIds: [],
  ticketTypeIds: [],
  tagIds: [],
  createdFrom: '',
  createdTo: '',
  resolvedFrom: '',
  resolvedTo: '',
  closedFrom: '',
  closedTo: '',
  customFields: {},
};

export function countActiveFilters(filters: TicketFilterState): number {
  return (
    filters.statuses.length
    + filters.priorities.length
    + filters.sources.length
    + filters.agentIds.length
    + filters.groupIds.length
    + filters.companyIds.length
    + filters.ticketTypeIds.length
    + filters.tagIds.length
    + Object.values(filters.customFields).filter((v) => v.length > 0).length
    + (filters.createdFrom || filters.createdTo ? 1 : 0)
    + (filters.resolvedFrom || filters.resolvedTo ? 1 : 0)
    + (filters.closedFrom || filters.closedTo ? 1 : 0)
  );
}

const CUSTOM_FILTERABLE_RENDERERS = new Set(['dropdown', 'checkbox', 'radio', 'multiselect']);
const LEVEL_CUSTOM_FIELD_KEY = 'cf_level';
const COUNTRY_CUSTOM_FIELD_KEY = 'cf_channelapplication';

// Requested removal from the ticket filter drawer — these custom fields
// clutter the filter list and are not used to narrow ticket search results.
// "Implementation Status" was also requested but does not exist as a field
// (closest match in the registry is "Implementation Timeline", a different
// field) — left in place pending clarification.
const HIDDEN_CUSTOM_FILTER_FIELD_KEYS = new Set([
  'cf_sdpl_pic',                                                          // SDPL PIC
  'cf_sdpl_observation',                                                  // SDPL Observation
  'cf_acceptable_criteria_in_detail_bullet_points',                       // Acceptable criteria (In detail- Bullet points)
  'cf_business_inconvenience_negative_impact',                            // Business inconvenience (negative impact)
  'cf_current_business_purposeflow_in_brief_pertaining_to_the_current_system', // Current: Business purpose/flow (in brief pertaining to the current system)
  'cf_business_vertical',                                                 // Business Vertical
  'cf_main_beneficiary',                                                  // Main Beneficiary
  'cf_attribute',                                                         // Attribute
  'cf_title_request',                                                     // Title (Request)
  'cf_system_name',                                                       // System
  'cf_cr_category',                                                       // CR Category
  'cf_current_functionality',                                            // Current Functionality
  'cf_cr_type',                                                           // CR Type
  'cf_final_status',                                                     // Final Status
  'cf_type',                                                              // Issue Type (duplicate of the system Ticket type filter)
]);

interface TicketFiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: TicketFilterState;
  onApply: (filters: TicketFilterState) => void;
  agents: DropdownSelectOption[];
  groups: DropdownSelectOption[];
  ticketTypes: DropdownSelectOption[];
  tags: DropdownSelectOption[];
}

function DateRangeField({ label, from, to, onFromChange, onToChange }: {
  label: string;
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}) {
  const { t } = useI18n();
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-ink-muted">{label}</label>
      <div className="flex items-center gap-2">
        <Input type="date" value={from} onChange={(e) => onFromChange(e.target.value)} aria-label={t('tickets.filterDateFrom', { label })} />
        <span className="text-xs text-ink-subtle">{t('tickets.filterDateTo2')}</span>
        <Input type="date" value={to} onChange={(e) => onToChange(e.target.value)} aria-label={t('tickets.filterDateTo', { label })} />
      </div>
    </div>
  );
}

export function TicketFiltersDrawer({
  open,
  onClose,
  filters,
  onApply,
  agents,
  groups,
  ticketTypes,
  tags,
}: TicketFiltersDrawerProps) {
  const { t, tOr } = useI18n();
  const [draft, setDraft] = React.useState<TicketFilterState>(filters);
  const [registryFields, setRegistryFields] = React.useState<TicketFieldRegistryField[]>([]);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  React.useEffect(() => {
    getTicketFields().then(setRegistryFields).catch(() => setRegistryFields([]));
  }, []);

  // ponytail: the Level MultiSelect stores dropdown option UUIDs, not labels
  // like "L1 RO". Resolve them through the Level field's rawChoices so
  // filterGroupsByLevel can pattern-match country codes in the label.
  const levelField = registryFields.find((f) => f.fieldKey === LEVEL_CUSTOM_FIELD_KEY);
  const levelChoices = levelField?.rawChoices ?? [];
  const countryChoices = registryFields.find((f) => f.fieldKey === COUNTRY_CUSTOM_FIELD_KEY)?.rawChoices ?? [];
  function resolveLabels(values: string[], choices: typeof levelChoices): string[] {
    if (values.length === 0 || choices.length === 0) return values;
    return values.map((v) => choices.find((c) => c.value === v || c.id === v || c.label === v)?.label ?? v);
  }
  // Group options narrow by the selected Country and Level together.
  function groupsMatching(customFields: Record<string, string[]>) {
    return filterGroupsByCountryAndLevel(
      groups,
      resolveLabels(customFields[COUNTRY_CUSTOM_FIELD_KEY] ?? [], countryChoices),
      resolveLabels(customFields[LEVEL_CUSTOM_FIELD_KEY] ?? [], levelChoices),
    );
  }

  // Level choices narrow by the selected Country.
  function levelsForCountries(countryValues: string[]) {
    return filterLevelsByCountry(
      levelChoices.map((choice) => ({ value: choice.value, label: choice.label })),
      resolveLabels(countryValues, countryChoices),
    );
  }

  React.useEffect(() => {
    if (!open || groups.length === 0) return;
    setDraft((current) => {
      const matchingGroups = groupsMatching(current.customFields);
      if (matchingGroups.length === groups.length) return current;
      const matchingGroupIds = new Set(matchingGroups.map((group) => group.value));
      const groupIds = current.groupIds.filter((groupId) => matchingGroupIds.has(groupId));
      if (groupIds.length === current.groupIds.length) return current;
      return { ...current, groupIds };
    });
  }, [groups, open, levelChoices, countryChoices]);

  const statusField = registryFields.find((field) => field.sourceType === 'default_status');
  const configuredStatusOptions = (statusField?.rawChoices ?? [])
    .filter((choice) => choice.active !== false)
    .map((choice) => ({
      value: choice.value,
      label: localizeLabel('status', choice.value, STATUS_MAP[choice.value]?.label ?? choice.label, tOr, choice.label),
    }));
  const statusOptions: DropdownSelectOption[] = configuredStatusOptions.length > 0
    ? configuredStatusOptions
    : Object.entries(STATUS_MAP).map(([value, meta]) => ({ value, label: localizeLabel('status', value, meta.label, tOr) }));
  const priorityOptions: DropdownSelectOption[] = Object.entries(PRIORITY_MAP).map(([value, meta]) => ({ value, label: localizeLabel('priority', value, meta.label, tOr) }));
  const sourceOptions: DropdownSelectOption[] = Object.entries(SOURCE_MAP).map(([value, meta]) => ({ value, label: localizeLabel('source', value, meta.label, tOr) }));

  const customFilterFields = registryFields.filter((field) => (
    field.isActive !== false
    && CUSTOM_FILTERABLE_RENDERERS.has(field.rendererType)
    && (field.rawChoices?.length ?? 0) > 0
    && !field.sourceType.startsWith('default_')
    && !HIDDEN_CUSTOM_FILTER_FIELD_KEYS.has(field.fieldKey)
  ));
  const matchingGroups = groupsMatching(draft.customFields);

  const lowerSearch = search.trim().toLowerCase();
  const matchesSearch = (label: string) => !lowerSearch || label.toLowerCase().includes(lowerSearch);

  function setField<K extends keyof TicketFilterState>(key: K, value: TicketFilterState[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function setCustomField(fieldKey: string, values: string[]) {
    setDraft((prev) => {
      let next = { ...prev, customFields: { ...prev.customFields, [fieldKey]: values } };
      if (fieldKey !== LEVEL_CUSTOM_FIELD_KEY && fieldKey !== COUNTRY_CUSTOM_FIELD_KEY) return next;

      // Switching Country drops any selected Level that belongs to another country.
      if (fieldKey === COUNTRY_CUSTOM_FIELD_KEY) {
        const allowedLevels = new Set(levelsForCountries(values).map((option) => option.value));
        const selectedLevels = next.customFields[LEVEL_CUSTOM_FIELD_KEY] ?? [];
        if (selectedLevels.some((level) => !allowedLevels.has(level))) {
          next = {
            ...next,
            customFields: { ...next.customFields, [LEVEL_CUSTOM_FIELD_KEY]: selectedLevels.filter((level) => allowedLevels.has(level)) },
          };
        }
      }

      const nextMatchingGroups = groupsMatching(next.customFields);
      if (nextMatchingGroups.length === groups.length) return next;
      const matchingGroupIds = new Set(nextMatchingGroups.map((group) => group.value));
      return {
        ...next,
        groupIds: prev.groupIds.filter((groupId) => matchingGroupIds.has(groupId)),
      };
    });
  }

  function handleApply() {
    onApply(draft);
    onClose();
  }

  function handleReset() {
    setDraft(EMPTY_TICKET_FILTERS);
    onApply(EMPTY_TICKET_FILTERS);
    onClose();
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={<span className="flex items-center gap-2"><ListFilter className="h-4 w-4" aria-hidden="true" />{t('tickets.filtersDrawerTitle')}</span>}
      widthClassName="max-w-sm"
      footer={
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={handleReset} className="text-sm text-ink-subtle hover:text-ink-muted">
            {t('tickets.filtersClearAll')}
          </button>
          <Button className="flex-1" onClick={handleApply}>{t('tickets.filtersApply')}</Button>
        </div>
      }
    >
      <div className="space-y-5 p-5">
        <Input
          placeholder={t('tickets.filtersSearchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('tickets.filtersSearchAria')}
        />

        {matchesSearch(t('tickets.filterAgents')) && (
          <MultiSelect label={t('tickets.filterAgents')} options={agents} value={draft.agentIds} onChange={(v) => setField('agentIds', v)} placeholder={t('tickets.filterAnyAgent')} />
        )}
        {matchesSearch(t('tickets.filterGroups')) && (
          <MultiSelect label={t('tickets.filterGroups')} options={matchingGroups} value={draft.groupIds} onChange={(v) => setField('groupIds', v)} placeholder={t('tickets.filterAnyGroup')} />
        )}
        {matchesSearch(t('tickets.filterStatus')) && (
          <MultiSelect label={t('tickets.filterStatus')} options={statusOptions} value={draft.statuses} onChange={(v) => setField('statuses', v)} placeholder={t('tickets.filterAnyStatus')} />
        )}
        {matchesSearch(t('tickets.filterPriority')) && (
          <MultiSelect label={t('tickets.filterPriority')} options={priorityOptions} value={draft.priorities} onChange={(v) => setField('priorities', v)} placeholder={t('tickets.filterAnyPriority')} />
        )}
        {matchesSearch(t('tickets.filterSource')) && (
          <MultiSelect label={t('tickets.filterSource')} options={sourceOptions} value={draft.sources} onChange={(v) => setField('sources', v)} placeholder={t('tickets.filterAnySource')} />
        )}
        {matchesSearch(t('tickets.filterTicketType')) && ticketTypes.length > 0 && (
          <MultiSelect label={t('tickets.filterTicketType')} options={ticketTypes} value={draft.ticketTypeIds} onChange={(v) => setField('ticketTypeIds', v)} placeholder={t('tickets.filterAnyType')} />
        )}
        {matchesSearch(t('tickets.filterTags')) && tags.length > 0 && (
          <MultiSelect label={t('tickets.filterTags')} options={tags} value={draft.tagIds} onChange={(v) => setField('tagIds', v)} placeholder={t('tickets.filterAnyTag')} />
        )}

        {matchesSearch(t('tickets.filterCreated')) && (
          <DateRangeField label={t('tickets.filterCreated')} from={draft.createdFrom} to={draft.createdTo} onFromChange={(v) => setField('createdFrom', v)} onToChange={(v) => setField('createdTo', v)} />
        )}
        {matchesSearch(t('tickets.filterResolvedAt')) && (
          <DateRangeField label={t('tickets.filterResolvedAt')} from={draft.resolvedFrom} to={draft.resolvedTo} onFromChange={(v) => setField('resolvedFrom', v)} onToChange={(v) => setField('resolvedTo', v)} />
        )}
        {matchesSearch(t('tickets.filterClosedAt')) && (
          <DateRangeField label={t('tickets.filterClosedAt')} from={draft.closedFrom} to={draft.closedTo} onFromChange={(v) => setField('closedFrom', v)} onToChange={(v) => setField('closedTo', v)} />
        )}

        {customFilterFields.filter((field) => matchesSearch(field.label)).map((field) => (
          <MultiSelect
            key={field.fieldKey}
            label={field.label}
            options={field.fieldKey === LEVEL_CUSTOM_FIELD_KEY
              ? levelsForCountries(draft.customFields[COUNTRY_CUSTOM_FIELD_KEY] ?? [])
              : (field.rawChoices ?? []).map((choice) => ({ value: choice.value, label: choice.label }))}
            value={draft.customFields[field.fieldKey] ?? []}
            onChange={(v) => setCustomField(field.fieldKey, v)}
            placeholder={t('tickets.filterAny')}
          />
        ))}
      </div>
    </SlideOver>
  );
}
